import { KeyboardEvent, TouchEvent, useEffect, useRef, useState } from 'react'
import { Box, CircularProgress, IconButton, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

export type CarouselItem = {
  url: string
  width?: number
  height?: number
}

type CarouselProps = {
  data: CarouselItem[]
  loading?: boolean
  initialIndex?: number
  itemLabel?: string
  emptyLabel?: string
  height?: string | number
  autoFocus?: boolean
}

type NavigationButtonProps = {
  direction: 'previous' | 'next'
  onClick: () => void
}

const SWIPE_THRESHOLD = 40

const ICON_SX = {
  fontSize: 12,
  lineHeight: 1,
  WebkitFontSmoothing: 'antialiased',
  MozOsxFontSmoothing: 'grayscale'
}

const CENTERED_OVERLAY_SX = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}

const NavigationButton = ({ direction, onClick }: NavigationButtonProps) => {
  const { t } = useTranslation()
  const isPrevious = direction === 'previous'

  return (
    <IconButton
      onClick={onClick}
      aria-label={t(`actions.${direction}`)}
      sx={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        ...(isPrevious ? { left: 8 } : { right: 8 }),
        width: 32,
        height: 32,
        backgroundColor: '#fff',
        border: '1px solid #E4EEFA',
        color: '#2570EB',
        '&:hover': { backgroundColor: '#F2F8FD' }
      }}
    >
      <Box component="i" className={`fa-solid fa-chevron-${isPrevious ? 'left' : 'right'}`} sx={ICON_SX} />
    </IconButton>
  )
}

const Carousel = ({
  data,
  loading = false,
  initialIndex = 0,
  itemLabel = '',
  emptyLabel = '',
  height = 'clamp(320px, 60vh, 480px)',
  autoFocus = false
}: CarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const containerRef = useRef<HTMLDivElement>(null)
  const touchStartX = useRef<number | null>(null)

  const itemsCount = data.length
  const activeIndex = Math.min(currentIndex, Math.max(itemsCount - 1, 0))
  const activeItem = data[activeIndex]
  const hasSeveralItems = itemsCount > 1

  const getItemLabel = (index: number) => `${itemLabel} ${index + 1}`.trim()

  useEffect(() => {
    setCurrentIndex(initialIndex)
  }, [initialIndex])

  useEffect(() => {
    if (!autoFocus) return
    containerRef.current?.focus()
  }, [autoFocus])

  const handlePrevious = () => {
    if (!hasSeveralItems) return
    setCurrentIndex(activeIndex === 0 ? itemsCount - 1 : activeIndex - 1)
  }

  const handleNext = () => {
    if (!hasSeveralItems) return
    setCurrentIndex(activeIndex === itemsCount - 1 ? 0 : activeIndex + 1)
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      handlePrevious()
      return
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      handleNext()
    }
  }

  const handleTouchStart = (event: TouchEvent) => {
    touchStartX.current = event.changedTouches[0].clientX
  }

  const handleTouchEnd = (event: TouchEvent) => {
    if (touchStartX.current == null) return
    const delta = event.changedTouches[0].clientX - touchStartX.current
    if (delta > SWIPE_THRESHOLD) handlePrevious()
    if (delta < -SWIPE_THRESHOLD) handleNext()
    touchStartX.current = null
  }

  return (
    <Stack
      ref={containerRef}
      tabIndex={0}
      role="region"
      aria-roledescription="carousel"
      aria-label={itemLabel || undefined}
      onKeyDown={handleKeyDown}
      sx={{
        gap: 2,
        outline: 'none',
        '&:focus-visible > .carousel-viewport': {
          outline: '2px solid #2570EB',
          outlineOffset: 2
        }
      }}
    >
      <Box
        className="carousel-viewport"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        sx={{
          position: 'relative',
          width: '100%',
          height,
          backgroundColor: '#FAFAFA',
          borderRadius: '16px',
          overflow: 'hidden',
          flexShrink: 0
        }}
      >
        {loading && (
          <Box sx={CENTERED_OVERLAY_SX}>
            <CircularProgress size={28} sx={{ color: '#2570EB' }} />
          </Box>
        )}

        {!loading && !itemsCount && (
          <Box sx={CENTERED_OVERLAY_SX}>
            <Typography sx={{ fontSize: 12, color: '#989898' }}>
              {emptyLabel}
            </Typography>
          </Box>
        )}

        {!loading && activeItem && (
          <Box
            component="img"
            src={activeItem.url}
            alt={getItemLabel(activeIndex)}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              userSelect: 'none',
              py: 2,
              px: hasSeveralItems ? 6 : 2
            }}
          />
        )}

        {!loading && hasSeveralItems && (
          <>
            <NavigationButton direction="previous" onClick={handlePrevious} />
            <NavigationButton direction="next" onClick={handleNext} />
          </>
        )}
      </Box>

      {!loading && hasSeveralItems && (
        <Stack alignItems="center" gap={1}>
          <Typography aria-live="polite" sx={{ fontSize: 12, fontWeight: 500, color: '#656565' }}>
            {activeIndex + 1} / {itemsCount}
          </Typography>
          <Stack direction="row" gap={0.75} justifyContent="center" flexWrap="wrap">
            {data.map((item, index) => {
              const isActive = index === activeIndex

              return (
                <Box
                  key={`${item.url}-${index}`}
                  component="button"
                  type="button"
                  aria-label={getItemLabel(index)}
                  aria-current={isActive}
                  onClick={() => setCurrentIndex(index)}
                  sx={{
                    width: isActive ? 16 : 8,
                    height: 8,
                    borderRadius: '8px',
                    border: 'none',
                    p: 0,
                    backgroundColor: isActive ? '#2570EB' : '#E4EEFA',
                    cursor: 'pointer',
                    transition: 'width 160ms ease, background-color 160ms ease'
                  }}
                />
              )
            })}
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

export default Carousel
